import React, {useCallback} from 'react';
import {Context} from '../context';
import Button from './UI/Button';
import Card from './UI/Card';
import {
  getPaymentMethodIcon,
  logger,
  paymentMethodExpiredStyling,
  showMsToClock
} from '../helperFunctions';
import {oneTimePayment, ONE_TIME_PAYMENT_DURATION} from '../settings';

export default function Cards() {
  const {
    processor,
    account,
    fetchPaymentToken,
    addPaymentMethod,
    removePaymentMethod,
    selectedCardID,
    setSelectedCardID,
    content,
    loading,
    setLoading,
    showError,
    setSnackBarMessage,
    localeText,
    oneTimePaymentToken,
    setOneTimePaymentToken,
    oneTimePaymentTimeLeft,
    addOneTimePaymentMethod,
    removeOneTimePaymentMethod
  } = React.useContext(Context);

  const [hostedFields, setHostedFields] = React.useState(null);
  const [NICiFrame, setNICiFrame] = React.useState(null);
  const [loadingNICiFrameMessage, setLoadingNICiFrameMessage] =
    React.useState(null);

  const braintreeValidateAndAddCard = useCallback(async () => {
    if (hostedFields === null) {
      showError(
        'rw_msg_error_payment_fields_expired',
        'Payment fields have become expired, please refresh the page'
      );
      return;
    }

    const state = hostedFields.getState();
    const invalidFields = Object.keys(state.fields).filter(
      key => !state.fields[key].isValid
    );
    if (invalidFields.length > 0) {
      showError(
        // TODO check if error phrase are human readable, else update/add to phrase data
        'Some payment input fields are invalid: ' +
          invalidFields.join(', ') +
          '. Please correct them before submitting.'
      );
      return;
    }

    hostedFields
      .tokenize({
        vault: true
      })
      .then(ret => {
        addPaymentMethod({
          nonce: ret.nonce,
          nickname: ret.description
        });
      })
      .catch(err => {
        showError(err.code, err.message);
      });
  }, [hostedFields, addPaymentMethod, showError]);

  const onMessage = event => {
    if (event.data === null) return;
    if (`${event.origin}`.slice(-10) !== 'nicusa.com') return;

    if (event.data === 'Error') {
      logger(event);
    } else if (event.data === 'CardDeclined') {
      setSnackBarMessage('Card Declined'); // kbfu needs a phrase
    } else if (event.data === 'Timeout') {
      setSnackBarMessage('payment-failure-timeout');
    } else if (event.data === 'AddMode' || event.data === 'CacheMode') {
      setLoadingNICiFrameMessage(null);
    } else if (event.data === 'PaymentAccountAdded') {
      setNICiFrame(null);
      addPaymentMethod({
        nonce: 'token',
        nickname: 'nickname'
      });
    } else if (event.data === 'PaymentAccountCached') {
      if (oneTimePaymentToken !== null) {
        setSelectedCardID(oneTimePaymentToken.token);
        addOneTimePaymentMethod();
      }
      setNICiFrame(null);
      setLoading(false);
    } else {
      logger(event);
    }
  };

  React.useEffect(() => {
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line
  }, [oneTimePaymentToken]);

  React.useEffect(() => {
    if (
      processor === 'nic' &&
      (selectedCardID === 'addCard' || selectedCardID === 'oneTimeCard')
    ) {
      setLoadingNICiFrameMessage('Loading payment interface ...');
      setNICiFrame(null);
      fetchPaymentToken().then(paymentToken => {
        if (paymentToken === undefined || paymentToken === '') {
          logger('paymentToken invalid');
          return;
        }
        if (paymentToken.errors) {
          showError(paymentToken.errors);
          logger({errors: paymentToken.errors});
          return;
        }
        try {
          const parsedPaymentToken = JSON.parse(atob(paymentToken));
          if (selectedCardID === 'oneTimeCard') {
            setOneTimePaymentToken({
              token: parsedPaymentToken.oneTimePaymentMethodUuid,
              expires: new Date(
                new Date().getTime() + ONE_TIME_PAYMENT_DURATION
              ),
              saved: false
            });
          }
          setNICiFrame(
            selectedCardID === 'oneTimeCard'
              ? parsedPaymentToken.oneTimePaymentMethodIFrame
              : parsedPaymentToken.addPaymentMethodIFrame
          );
        } catch (err) {
          showError(err);
          logger({err, paymentToken});
        }
      });
    } else if (
      processor === 'braintree' &&
      hostedFields === null &&
      selectedCardID === 'addCard'
    ) {
      fetchPaymentToken().then(paymentToken => {
        if (paymentToken.errors) {
          showError(paymentToken.errors);
          logger({errors: paymentToken.errors});
          return;
        }
        // eslint-disable-next-line
        braintree.client.create(
          {
            authorization: paymentToken
          },
          function (err, clientInstance) {
            if (err) {
              logger(err);
              return;
            }
            // eslint-disable-next-line
            braintree.hostedFields.create(
              {
                client: clientInstance,
                styles: {
                  input: {
                    'font-size': '16px',
                    'font-family': 'courier, monospace',
                    'font-weight': 'lighter',
                    color: '#aaa'
                  },
                  ':focus': {color: '#bbb'},
                  '.valid': {color: '#8bdda8'},
                  '.invalid': {color: '#dd8ba8'}
                },
                fields: {
                  number: {
                    selector: '.hosted-field.card-number',
                    placeholder: localeText(
                      'rw_paymentcards_placeholder_card_number',
                      'Card Number'
                    )
                  },
                  cvv: {
                    selector: '.hosted-field.cvv',
                    placeholder: localeText(
                      'rw_paymentcards_placeholder_cvv',
                      'CVV'
                    )
                  },
                  expirationDate: {
                    selector: '.hosted-field.expiration-date',
                    placeholder: localeText(
                      'rw_paymentcards_placeholder_exp_date',
                      'Exp. Date (MM/YYYY)'
                    )
                  },
                  postalCode: {
                    selector: '.hosted-field.postal-code',
                    placeholder: localeText(
                      'rw_paymentcards_placeholder_zip_code_postal_code',
                      'Zip Code / Postal Code'
                    )
                  }
                }
              },
              function (err, hostedFields) {
                if (err) logger(err);
                setHostedFields(hostedFields);
              }
            );
          }
        );
      });
    }
    // eslint-disable-next-line
  }, [selectedCardID]);

  const clickDelete = paymentMethodId => {
    if (oneTimePaymentToken && oneTimePaymentToken?.token === paymentMethodId) {
      removeOneTimePaymentMethod();
      return setSnackBarMessage('Card Removed');
    }
    removePaymentMethod(paymentMethodId);
  };

  if (!account) {
    return (
      <div>
        {localeText(
          'rw_paymentcards_text_not_logged_in',
          'You are not logged in.'
        )}
      </div>
    );
  }

  return (
    <>
      <div className='flex flex-col gap-2'>
        {account?.paymentMethods === null && (
          <div>{localeText('rw_paymentcards_text_loading', 'Loading ...')}</div>
        )}
        {account?.paymentMethods &&
          account?.paymentMethods
            .filter(
              paymentMethod => paymentMethod.nickname !== 'Transit Account'
            )
            .map((paymentMethod, index) => {
              const paymentMethodSrc = getPaymentMethodIcon(
                content,
                paymentMethod
              );
              return (
                <Card
                  onClick={() => setSelectedCardID(paymentMethod.id)}
                  cardId={paymentMethod.id}
                  key={index}
                  className={`${
                    paymentMethod.id === selectedCardID &&
                    'bg-secondary border-bg-dark selected-payment-card'
                  } text-sm sm:text-base`}
                >
                  <div className='flex items-center gap-4 checked:bg-dark'>
                    {paymentMethodSrc && (
                      <img
                        alt={localeText(
                          'rw_paymentcards_alt_payment_method_icon',
                          'Payment method icon'
                        )}
                        src={paymentMethodSrc}
                        className='h-8 w-12'
                      />
                    )}
                    {paymentMethod.nickname} ****{paymentMethod.accountNumber}
                  </div>
                  {paymentMethod.processor !== 'STORED_VALUE' && (
                    <React.Fragment>
                      <div
                        className={`ml-auto pr-2 sm:pr-5 ${paymentMethodExpiredStyling(
                          paymentMethod
                        )}`}
                      >
                        {localeText('rw_paymentcards_text_exp', 'exp')}{' '}
                        {paymentMethod.expirationMonth}/
                        {paymentMethod.expirationYear}
                      </div>
                      <Button
                        loading={loading}
                        onClick={() => clickDelete(paymentMethod?.id)}
                        className='py-2'
                      >
                        {localeText('rw_paymentcards_button_delete', 'Delete')}
                      </Button>
                    </React.Fragment>
                  )}
                </Card>
              );
            })}
        {oneTimePaymentToken?.saved && (
          <Card
            onClick={() => setSelectedCardID(oneTimePaymentToken.token)}
            cardId='saved-one-time-card'
            className={`${
              oneTimePaymentToken.token === selectedCardID &&
              'bg-secondary border-bg-dark selected-payment-card'
            }`}
          >
            <div className='flex items-center gap-4 checked:bg-dark'>
              {localeText(
                'rw_paymentcards_text_one_time_payment_saved_card',
                'One Time Payment Saved Card'
              )}
            </div>
            <div className='ml-auto pr-2 sm:pr-5'>
              {localeText(
                'expires in ' // TODO updated phrase data 'rw_paymentcards_text_expires_15_minutes',
              )}
              {showMsToClock(oneTimePaymentTimeLeft)}
            </div>
            <Button
              loading={loading}
              onClick={() => clickDelete(oneTimePaymentToken?.token)}
              className='py-2'
            >
              {localeText('rw_paymentcards_button_delete', 'Delete')}
            </Button>
          </Card>
        )}
        <Card
          cardId='add-card'
          onClick={() => setSelectedCardID('addCard')}
          key='addCard'
          className={
            'addCard' === selectedCardID && 'bg-secondary border-bg-dark'
          }
        >
          <div className='my-2 items-center gap-4 checked:bg-dark'>
            {localeText('rw_paymentcards_button_add_new_card', 'Add New Card')}
          </div>
        </Card>
        {oneTimePayment && !oneTimePaymentToken?.saved && (
          <Card
            cardId='add-one-time-card'
            onClick={() => setSelectedCardID('oneTimeCard')}
            key='oneTimeCard'
            className={
              'oneTimeCard' === selectedCardID && 'bg-secondary border-bg-dark'
            }
          >
            <div className='my-2 items-center gap-4 checked:bg-dark'>
              {localeText(
                'rw_paymentcards_button_one_time_payment',
                'One Time Payment'
              )}
            </div>
          </Card>
        )}
      </div>
      {processor === 'braintree' && (
        <div className={`${selectedCardID !== 'addCard' && 'hidden'}`}>
          <div style={{padding: '10px'}}>
            {localeText(
              'rw_paymentcards_text_enter_credit_card_details',
              'Enter Your Credit Card Details:'
            )}
          </div>
          <div className='braintree-card-entry'>
            <div className='paypal-container'></div>
            <div
              className='hosted-field card-number'
              style={{paddingLeft: '10px', height: '50px'}}
            ></div>
            <div
              className='hosted-field expiration-date'
              style={{
                paddingLeft: '10px',
                display: 'inline-block',
                height: '50px',
                width: '325px'
              }}
            ></div>
            <div
              className='hosted-field cvv'
              style={{
                display: 'inline-block',
                height: '50px',
                width: '125px'
              }}
            ></div>
            <div
              className='hosted-field postal-code'
              style={{paddingLeft: '10px', height: '50px'}}
            ></div>
            <div className='button-container'>
              <Button
                className='w-full'
                loading={loading}
                onClick={braintreeValidateAndAddCard}
              >
                {localeText(
                  'rw_paymentcards_button_add_new_card',
                  'Add New Card'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      {processor === 'nic' &&
        (selectedCardID === 'addCard' || selectedCardID === 'oneTimeCard') && (
          <div>
            {loadingNICiFrameMessage && (
              <div
                style={{
                  padding: '10px',
                  backgroundColor: '#fed',
                  borderRadius: '5px'
                }}
              >
                {loadingNICiFrameMessage}
              </div>
            )}
            {NICiFrame && (
              <iframe
                title='NIC add payment method'
                id='NICiFrame'
                src={NICiFrame}
                className='w-full h-full'
                scrolling='no'
                style={{
                  height: '1315px',
                  overflow: 'hidden',
                  border: '0px solid black'
                }}
              />
            )}
          </div>
        )}
    </>
  );
}
